// Value-exact box search over the document words: text match first, else
// numeric match tolerant of German formatting. The pipeline only delivers few,
// row-wide field boxes — this finds the tight box of the actual value.
import { Word } from '../types/job';
import { Box, hullOfWords } from './geometry';

const normalizeText = (value: unknown): string =>
  String(value ?? '')
    .replace(/\s+/g, '')
    .toLowerCase();

/**
 * German amounts, including SPACE (also thin/nbsp) as thousands separator
 * ("2 804,80") and signs with a gap ("- 1 340,00"). Inner whitespace is
 * stripped before parsing — otherwise the match would stop at the space.
 */
export function parseGermanNumber(text: unknown): number | null {
  const match = String(text).match(/-?\s*\d[\d.,\s\u00a0\u202f]*\d|-?\s*\d/);
  if (!match) return null;
  let s = match[0].replace(/[\s\u00a0\u202f]/g, '');
  const dot = s.includes('.');
  const comma = s.includes(',');
  if (dot && comma) {
    const decimal = s.lastIndexOf(',') > s.lastIndexOf('.') ? ',' : '.';
    s = s
      .split(decimal === ',' ? '.' : ',')
      .join('')
      .replace(decimal, '.');
  } else if (comma) {
    s = s.split(',').pop()?.length === 3 ? s.split(',').join('') : s.replace(',', '.');
  } else if (dot) {
    s = s.split('.').pop()?.length === 3 ? s.split('.').join('') : s;
  }
  const parsed = parseFloat(s);
  return Number.isNaN(parsed) ? null : parsed;
}

function numbersEqual(found: number, target: number, text: string): boolean {
  if (Math.abs(found - target) < 0.005) return true;
  // A fraction target ("0.03") printed as percent ("3 %") on the document.
  return (
    target > 0 &&
    target < 1 &&
    Math.abs(found - target * 100) < 0.005 &&
    text.includes('%')
  );
}

const readingOrder = (a: Word, b: Word): number =>
  Math.round(((a.y0 + a.y1) / 2) * 10) - Math.round(((b.y0 + b.y1) / 2) * 10) ||
  a.x0 - b.x0;

/**
 * Shortest contiguous word run (reading order) whose concatenated text equals
 * `value`. Base of both the document-wide metadata search and the row-limited
 * attribute search.
 */
export function matchWordRun(words: Word[], value: unknown): Word[] | null {
  const target = normalizeText(value);
  if (!target || !words.length) return null;
  const sorted = words.slice().sort(readingOrder);
  const norms = sorted.map((w) => normalizeText(w.text));
  let best: Word[] | null = null;
  for (let i = 0; i < sorted.length; i++) {
    if (!norms[i]) continue;
    let acc = '';
    for (let j = i; j < sorted.length; j++) {
      acc += norms[j];
      if (acc.length > target.length) break;
      if (acc === target) {
        if (!best || j - i + 1 < best.length) best = sorted.slice(i, j + 1);
        break;
      }
    }
  }
  return best;
}

/**
 * Tight box of a value anywhere in the document: shortest text run over all
 * pages, numeric fallback (thousands/decimal separator and % tolerant) so
 * amounts like "3.322,13" ↔ "3322.13" still hit.
 */
export function findValueBox(
  value: unknown,
  words: Word[]
): (Box & { page: number }) | null {
  const target = normalizeText(value);
  if (!target || !words.length) return null;

  const byPage = new Map<number, Word[]>();
  for (const word of words) {
    const pageWords = byPage.get(word.page) ?? [];
    pageWords.push(word);
    byPage.set(word.page, pageWords);
  }
  const pages = [...byPage.keys()].sort((a, b) => a - b);

  let best: { page: number; run: Word[] } | null = null;
  for (const page of pages) {
    const hit = matchWordRun(byPage.get(page) ?? [], value);
    if (hit && (!best || hit.length < best.run.length)) best = { page, run: hit };
  }
  if (!best) {
    const targetNumber = parseGermanNumber(value);
    if (targetNumber != null) {
      outer: for (const page of pages) {
        for (const word of byPage.get(page) ?? []) {
          const wordNumber = parseGermanNumber(word.text);
          if (wordNumber != null && numbersEqual(wordNumber, targetNumber, word.text)) {
            best = { page, run: [word] };
            break outer;
          }
        }
      }
    }
  }
  if (!best) return null;
  return { page: best.page, ...hullOfWords(best.run) };
}
